import { Injectable } from '@angular/core';

/** Stop waiting on stalled ICE gathering and send whatever candidates exist. */
const ICE_GATHERING_TIMEOUT_MS = 2000;

/**
 * Negotiates a MediaMTX WHEP session and binds the remote MediaStream to a
 * native video element. Single request: POST /{path}/whep with an SDP offer
 * that already carries its ICE candidates (no trickle session).
 */
@Injectable({ providedIn: 'root' })
export class WhepPlayerService {
  private readonly frames = new WeakMap<
    HTMLVideoElement,
    { width: number; height: number; session: object }
  >();

  frameSizeFor(video: HTMLVideoElement): { width: number; height: number } | undefined {
    const frame = this.frames.get(video);
    if (!frame) {
      return undefined;
    }
    return { width: frame.width, height: frame.height };
  }

  async attach(video: HTMLVideoElement, playbackUrl: string): Promise<() => void> {
    const peer = new RTCPeerConnection();
    peer.addTransceiver('video', { direction: 'recvonly' });
    peer.addTransceiver('audio', { direction: 'recvonly' });

    let ownStream: MediaStream | null = null;
    const session = {};

    peer.ontrack = (event) => {
      const stream = event.streams[0] ?? new MediaStream([event.track]);
      // The template's static `muted` attribute only sets defaultMuted, so the
      // property stays false and Chrome blocks autoplay of a stream with audio.
      video.muted = true;
      ownStream = stream;
      video.srcObject = stream;
      void video.play()?.catch((error: DOMException) => {
        console.warn(`[whep] autoplay blocked: ${error?.name ?? error}`);
      });
    };

    // A superseded session must not detach the stream a newer one already bound.
    const releaseVideo = (): void => {
      if (video.srcObject === ownStream) {
        video.srcObject = null;
      }
      const current = this.frames.get(video);
      if (current?.session === session) {
        this.frames.delete(video);
      }
    };

    try {
      const offer = await peer.createOffer();
      await peer.setLocalDescription(offer);
      await waitForIceGathering(peer);
      const sdp = peer.localDescription?.sdp ?? offer.sdp;
      const response = await fetch(toWhepUrl(playbackUrl), {
        method: 'POST',
        headers: { 'Content-Type': 'application/sdp' },
        body: sdp,
      });
      if (!response.ok) {
        throw new Error(`WHEP negotiation failed (${response.status})`);
      }
      const answerSdp = await response.text();
      await peer.setRemoteDescription({ type: 'answer', sdp: answerSdp });
      const frame = parseMediaMtxSdpFrameSize(answerSdp);
      if (frame) {
        this.frames.set(video, { ...frame, session });
      } else {
        this.frames.delete(video);
      }
    } catch (error) {
      peer.close();
      releaseVideo();
      throw error;
    }

    return () => {
      peer.close();
      releaseVideo();
    };
  }
}

function waitForIceGathering(peer: RTCPeerConnection): Promise<void> {
  if (peer.iceGatheringState === 'complete') {
    return Promise.resolve();
  }

  return new Promise((resolve) => {
    let timer: ReturnType<typeof setTimeout>;
    const finish = (): void => {
      clearTimeout(timer);
      peer.removeEventListener('icegatheringstatechange', onGatheringChange);
      resolve();
    };
    const onGatheringChange = (): void => {
      if (peer.iceGatheringState === 'complete') {
        finish();
      }
    };
    timer = setTimeout(finish, ICE_GATHERING_TIMEOUT_MS);
    peer.addEventListener('icegatheringstatechange', onGatheringChange);
  });
}

function parseMediaMtxSdpFrameSize(sdp: string): { width: number; height: number } | undefined {
  const framesize = /(?:^|\r?\n)a=framesize:\d+\s+(\d+)-(\d+)/i.exec(sdp);
  if (framesize) {
    const width = Number(framesize[1]);
    const height = Number(framesize[2]);
    if (width > 0 && height > 0) {
      return { width, height };
    }
  }
  const imageattr = /(?:^|\r?\n)a=imageattr:[^\r\n]*\[x=(\d+),y=(\d+)\]/i.exec(sdp);
  if (imageattr) {
    const width = Number(imageattr[1]);
    const height = Number(imageattr[2]);
    if (width > 0 && height > 0) {
      return { width, height };
    }
  }
  return undefined;
}

function toWhepUrl(playbackUrl: string): string {
  const trimmed = playbackUrl.trim();
  if (trimmed.endsWith('/whep')) {
    return trimmed.replace(/\/+$/, '');
  }
  const queryIndex = trimmed.indexOf('?');
  const pathPart = queryIndex >= 0 ? trimmed.slice(0, queryIndex) : trimmed;
  const query = queryIndex >= 0 ? trimmed.slice(queryIndex) : '';
  const whepPath = `${pathPart.replace(/\/+$/, '')}/whep`;
  return `${whepPath}${query}`;
}
